import { createAsyncThunk } from '@reduxjs/toolkit';
import api from 'api';
// Corrected import path
import {
  ApplyCancellationPromotionRequestDto,
  ChangePromocodeRequestDto,
  DeletePromocodeRequestDto,
  PauseSubscriptionRequestDto,
  PaymentDto,
  PaymentDtoLevelEnum,
  PurchaseCourseInInstallmentDto,
  PurchaseCourseRequestDto,
  RestoreInstallmentPaymentDto,
  StripePaymentSource,
  SubscribeToCourseRequestDto,
  UnpauseSubscriptionRequestDto,
  UpdateInstallmentPromocodeDto,
  ValidateConfirmationTokenRequestDto,
  ValidateConfirmationTokenResponseDto,
} from 'api/generated';
import { RequestConfig } from 'api/https-client';

import {
  ChangePromoCodeResponse,
  CreateInstallmentPaymentResponse,
  DefaultRejectValue,
  ExtraParamsThunkType,
  PaymentsResponse,
  ValidateInstallmentPricingResponse,
} from './types';

const toRejectValue = (error: unknown): DefaultRejectValue => {
  if (!error) {
    return { message: 'Request failed' };
  }

  if (typeof error === 'string') {
    return { message: error };
  }

  if (error instanceof Error) {
    const errWithStatus = error as Error & { status?: number; statusCode?: number; data?: unknown };
    const data =
      typeof errWithStatus.data === 'object' && errWithStatus.data !== null
        ? (errWithStatus.data as { message?: unknown })
        : undefined;
    const maybeMessage = data?.message;
    const dataMessage =
      typeof maybeMessage === 'string' ? maybeMessage : Array.isArray(maybeMessage) ? maybeMessage : undefined;

    return {
      message: dataMessage || errWithStatus.message || 'Request failed',
      statusCode: errWithStatus.statusCode || errWithStatus.status,
    };
  }

  if (typeof error === 'object') {
    const maybeError = error as { message?: unknown; status?: unknown; statusCode?: unknown };
    return {
      message:
        typeof maybeError.message === 'string' || Array.isArray(maybeError.message)
          ? (maybeError.message as string | string[])
          : 'Request failed',
      statusCode:
        typeof maybeError.statusCode === 'number'
          ? maybeError.statusCode
          : typeof maybeError.status === 'number'
          ? maybeError.status
          : undefined,
    };
  }

  return { message: 'Request failed' };
};

export const subscribeCourseThunk = createAsyncThunk<
  PaymentsResponse,
  SubscribeToCourseRequestDto & { useToken?: boolean },
  ExtraParamsThunkType<DefaultRejectValue>
>(
  'payments/subscribeToCourse',
  // @ts-ignore
  async (
    { courseId, level, period, source, promocode, useToken, impactClickId, googleClickId, abId },
    { rejectWithValue },
  ) => {
    const subscribeParams = {
      courseId,
      level,
      period,
      source,
      promocode,
      impactClickId,
      googleClickId,
      abId,
    };

    try {
      //@ts-ignore
      const response = await api.Payments.paymentsControllerSubscribeToCourse(subscribeParams, { useToken });

      const { data, status } = response;

      if (status > 399) {
        throw data;
      }

      return data;
    } catch (error) {
      return rejectWithValue(toRejectValue(error));
    }
  },
);

export const purchaseCourseThunk = createAsyncThunk<
  PaymentsResponse,
  PurchaseCourseRequestDto & { useToken?: boolean }, // useToken is already a parameter here
  ExtraParamsThunkType<DefaultRejectValue>
  // @ts-ignore
>(
  'payments/purchaseCourse',
  async ({ courseId, source, promocode, useToken, level, impactClickId, googleClickId, abId }, { rejectWithValue }) => {
    // Debug logging in Redux thunk
    console.log('Redux purchaseCourseThunk - Click IDs:', {
      impactClickId: impactClickId || 'MISSING',
      googleClickId: googleClickId || 'MISSING',
      abId: abId || 'MISSING',
      calledAt: new Date().toISOString(),
    });

    const purchaseParams = {
      courseId,
      source,
      promocode,
      level,
      impactClickId,
      googleClickId,
      abId,
    };
    try {
      //@ts-ignore
      const { data, status } = await api.Payments.paymentsControllerPurchaseCourse(purchaseParams, { useToken });

      if (status > 399) {
        throw data;
      }

      // Explicitly cast or transform data to PaymentsResponse
      return data as PaymentsResponse;
    } catch (error) {
      return rejectWithValue(toRejectValue(error));
    }
  },
);

export const purchaseCourseInstallmentThunk = createAsyncThunk<
  PaymentsResponse,
  PurchaseCourseInInstallmentDto & { useToken?: boolean }, // Add useToken here
  ExtraParamsThunkType<DefaultRejectValue>
>(
  'payments/purchaseCourseInstallment',
  // @ts-ignore
  async (
    { courseId, source, promocode, useFirstMonthDiscount, level, useToken, impactClickId, googleClickId, abId },
    { rejectWithValue },
  ) => {
    // Add useToken to params
    const purchaseParams = {
      courseId,
      source,
      promocode,
      useFirstMonthDiscount,
      level,
      impactClickId,
      googleClickId,
      abId,
    };
    try {
      // Pass useToken to the API call
      //@ts-ignore
      const { data, status } = await api.Payments.paymentsControllerPurchaseCourseInInstallment(
        purchaseParams,
        { useToken } as RequestConfig, // Correctly pass useToken and cast
      );

      if (status > 399) {
        throw data;
      }

      return data;
    } catch (error) {
      return rejectWithValue(toRejectValue(error));
    }
  },
);

// NEW: Fast pricing validation endpoint
export const validateInstallmentPricingThunk = createAsyncThunk<
  ValidateInstallmentPricingResponse,
  Omit<PurchaseCourseInInstallmentDto, 'impactClickId' | 'googleClickId' | 'abId'> & { useToken?: boolean },
  ExtraParamsThunkType<DefaultRejectValue>
>(
  'payments/validateInstallmentPricing',
  async ({ courseId, source, promocode, useFirstMonthDiscount, level, useToken }, { rejectWithValue }) => {
    const params = {
      courseId,
      source: source as StripePaymentSource,
      promocode,
      useFirstMonthDiscount,
      level: level as PaymentDtoLevelEnum,
    };

    try {
      const { data, status } = await api.Payments.paymentsControllerValidateInstallmentPricing(params, {
        useToken,
      } as RequestConfig);

      if (status > 399) {
        throw data;
      }

      const installmentPricingData = data as typeof data & {
        commitmentPaymentAmount?: number | null;
        commitmentPaymentDelayDays?: number | null;
      };

      // Convert DTO to local type
      return {
        totalAmount: data.totalAmount,
        monthlyAmount: data.monthlyAmount,
        firstMonthAmount: data.firstMonthAmount,
        commitmentPaymentAmount: installmentPricingData.commitmentPaymentAmount ?? null,
        commitmentPaymentDelayDays: installmentPricingData.commitmentPaymentDelayDays ?? null,
        coupons: data.coupons,
        courseId: data.courseId,
        directionId: data.directionId,
        level: data.level,
      };
    } catch (error) {
      return rejectWithValue(toRejectValue(error));
    }
  },
);

// NEW: Create payment intent with pre-validated amounts
export const createInstallmentPaymentThunk = createAsyncThunk<
  CreateInstallmentPaymentResponse,
  {
    courseId: string;
    totalAmount: number;
    monthlyAmount: number;
    firstMonthAmount: number | null;
    commitmentPaymentAmount?: number | null;
    commitmentPaymentDelayDays?: number | null;
    source: string;
    level: string;
    promocode?: string;
    useFirstMonthDiscount?: boolean;
    impactClickId?: string;
    googleClickId?: string;
    abId?: string;
    useToken?: boolean;
  },
  ExtraParamsThunkType<DefaultRejectValue>
>(
  'payments/createInstallmentPayment',
  async (
    {
      courseId,
      totalAmount,
      monthlyAmount,
      firstMonthAmount,
      commitmentPaymentAmount,
      commitmentPaymentDelayDays,
      source,
      level,
      promocode,
      useFirstMonthDiscount,
      impactClickId,
      googleClickId,
      abId,
      useToken,
    },
    { rejectWithValue },
  ) => {
    const params = {
      courseId,
      totalAmount,
      monthlyAmount,
      firstMonthAmount,
      commitmentPaymentAmount,
      commitmentPaymentDelayDays,
      source: source as StripePaymentSource,
      level: level as PaymentDtoLevelEnum,
      // The server recomputes pricing from these (validate-installment-pricing candidates);
      // dropping them made every manually-typed coupon 400 with "Pricing has changed".
      promocode,
      useFirstMonthDiscount,
      impactClickId,
      googleClickId,
      abId,
    };

    try {
      const { data, status } = await api.Payments.paymentsControllerCreateInstallmentPayment(params, {
        useToken,
        timeout: 60000,
      } as RequestConfig);

      if (status > 399) {
        throw data;
      }

      // Convert DTO to local type
      return {
        invoiceId: data.invoiceId,
        clientSecret: data.clientSecret,
      };
    } catch (error) {
      return rejectWithValue(toRejectValue(error));
    }
  },
);

export const changeUseFirstPromoCodeThunk = createAsyncThunk<
  ChangePromoCodeResponse,
  UpdateInstallmentPromocodeDto,
  ExtraParamsThunkType<DefaultRejectValue>
>(
  'payments/changeUseFirstPromoCode',
  // @ts-ignore
  async ({ promocode, invoiceId, useFirstMonthDiscount, directionId }, { rejectWithValue }) => {
    try {
      const { data, status } = await api.Payments.paymentsControllerUpdateInstallmentPromocode({
        promocode,
        invoiceId,
        useFirstMonthDiscount,
        directionId,
      });

      if (status > 399) {
        throw data;
      }

      return data;
    } catch (error) {
      return rejectWithValue(toRejectValue(error));
    }
  },
);

export const changePromocodeThunk = createAsyncThunk<
  ChangePromoCodeResponse,
  ChangePromocodeRequestDto,
  ExtraParamsThunkType<DefaultRejectValue>
>('payments/changePromoCode', async ({ promocode, invoiceId, directionId }, { rejectWithValue }) => {
  try {
    const { data, status } = await api.Payments.paymentsControllerChangePromocode({
      promocode,
      invoiceId,
      directionId,
    });

    if (status > 399) {
      throw data;
    }

    return data;
  } catch (error) {
    return rejectWithValue(toRejectValue(error));
  }
});

export const deletePromocodeThunk = createAsyncThunk<
  ChangePromoCodeResponse,
  DeletePromocodeRequestDto,
  ExtraParamsThunkType<DefaultRejectValue>
>('payments/changePromoCode', async ({ invoiceId, directionId }, { rejectWithValue }) => {
  try {
    const { data, status } = await api.Payments.paymentsControllerDeletePromocode({ invoiceId, directionId });

    if (status > 399) {
      throw data;
    }

    return data;
  } catch (error) {
    return rejectWithValue(toRejectValue(error));
  }
});

export const pauseSubscriptionThunk = createAsyncThunk<
  PaymentDto,
  PauseSubscriptionRequestDto,
  ExtraParamsThunkType<DefaultRejectValue>
>('payments/pauseSubscription', async ({ courseId, months }, { rejectWithValue }) => {
  try {
    const { data, status } = await api.Payments.paymentsControllerPauseSubscription({ courseId, months });

    if (status > 399) {
      throw data;
    }

    return data;
  } catch (error) {
    return rejectWithValue(toRejectValue(error));
  }
});

export const applyCancellationPromotionThunk = createAsyncThunk<
  { applied: boolean; alreadyApplied: boolean; message: string; couponId?: string },
  ApplyCancellationPromotionRequestDto,
  ExtraParamsThunkType<DefaultRejectValue>
>('payments/apply-cancellation-promotion', async ({ courseId }, { rejectWithValue }) => {
  try {
    const { data, status } = await api.Payments.paymentsControllerApplyCancellationPromotion({ courseId }, {
      useToken: true,
    } as RequestConfig);

    if (status > 399) {
      throw data;
    }

    return data as unknown as { applied: boolean; alreadyApplied: boolean; message: string; couponId?: string };
  } catch (error) {
    return rejectWithValue(toRejectValue(error));
  }
});

export const unpauseSubscriptionThunk = createAsyncThunk<
  PaymentDto,
  UnpauseSubscriptionRequestDto,
  ExtraParamsThunkType<DefaultRejectValue>
>('payments/unpause/subscription', async ({ courseId }, { rejectWithValue }) => {
  try {
    const { data, status } = await api.Payments.paymentsControllerUnpauseSubscription({ courseId });

    if (status > 399) {
      throw data;
    }

    return data;
  } catch (error) {
    return rejectWithValue(toRejectValue(error));
  }
});

export const unpauseInstallmentThunk = createAsyncThunk<
  PaymentsResponse,
  RestoreInstallmentPaymentDto,
  ExtraParamsThunkType<DefaultRejectValue>
  // @ts-ignore
>('payments/unpause/installment', async ({ courseId }, { rejectWithValue }) => {
  try {
    const { data, status } = await api.Payments.paymentsControllerRestoreInstallmentPayment({ courseId });

    if (status > 399) {
      throw data;
    }

    return data;
  } catch (error) {
    return rejectWithValue(toRejectValue(error));
  }
});

export const validateTokenThunk = createAsyncThunk<
  ValidateConfirmationTokenResponseDto,
  ValidateConfirmationTokenRequestDto & { useToken: boolean },
  ExtraParamsThunkType<DefaultRejectValue>
>('payments/validate-confirmation-token', async ({ confirmationToken, courseId, useToken }, { rejectWithValue }) => {
  const payload = {
    confirmationToken,
    courseId,
  };
  try {
    //@ts-ignore
    const { data, status } = await api.Payments.paymentsControllerValidateConfirmationToken(payload, { useToken });

    if (status > 399) {
      throw data;
    }

    return data;
  } catch (error) {
    return rejectWithValue(toRejectValue(error));
  }
});

export const actions = {
  subscribeCourse: subscribeCourseThunk,
  purchaseCourse: purchaseCourseThunk,
  unpauseInstallment: unpauseInstallmentThunk,
  unpauseSubscription: unpauseSubscriptionThunk,
  changePromoCode: changePromocodeThunk,
  deletePromoCode: deletePromocodeThunk,
  changeUseFirstPromoCode: changeUseFirstPromoCodeThunk,
  pauseSubscription: pauseSubscriptionThunk,
  applyCancellationPromotion: applyCancellationPromotionThunk,
  validateToken: validateTokenThunk,
  purchaseCourseInstallment: purchaseCourseInstallmentThunk,
  // NEW: Optimized two-phase actions
  validateInstallmentPricing: validateInstallmentPricingThunk,
  createInstallmentPayment: createInstallmentPaymentThunk,
};
